import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, Typography, Chip } from '@mui/material';
import FilterList from '@mui/icons-material/FilterList';
import { i18n } from '../../../../translate/i18n';
import { RootState } from '../../../../store/types';
import { IAutocompleteOption } from '../../../../models/iautocompleteOption';
import * as actions from '../../../../store/actions';

type Props = {
  openFiltersModal: CallableFunction,
}

const TableFilters: React.FC<Props> = ({ openFiltersModal }) => {
  const dispatch = useDispatch();

  const selectedSites = useSelector((state: any) => state.sites.selectedSitesPlaces);
  const filteredPlaces = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredPlaces);
  const filteredModels = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredModels);
  const filteredGroups = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredGroups);
  const filteredAreas = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredAreas);
  const filteredCompanies = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredCompanies);
  const filteredStatus = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredStatus);

  const deleteFilteredSite = (site: any) => {
    dispatch(actions.deleteFilteredSite(site));
  }

  const deleteFilteredPlaces = (place: IAutocompleteOption) => {
    dispatch(actions.createServiceRemovePlaceFilter(place));
  }

  const deleteFilteredGroups = (group: IAutocompleteOption) => {
    dispatch(actions.createServiceRemoveGroupFilter(group));
  }

  const deleteFilteredAreas = (area: IAutocompleteOption) => {
    dispatch(actions.createServiceRemoveAreaFilter(area));
  }

  const deleteFilteredCompanies = (company: IAutocompleteOption) => {
    dispatch(actions.createServiceRemoveCompanyFilter(company));
  }

  const deleteFilteredModels = (model: IAutocompleteOption) => {
    dispatch(actions.createServiceRemoveEquipmentModelFilter(model));
  }

  const deleteFilteredStatus = (key: string) => {
		if (key.includes('invalid.')) {
			const invalidKey = key.split('.')[1];
			dispatch(actions.createServiceStatusFilter({ ...filteredStatus, invalid: { ...filteredStatus.invalid, [invalidKey]: false } }));
		} else {
			dispatch(actions.createServiceStatusFilter({ ...filteredStatus, [key]: false }));
		}
	};

  const clearFilterButton = () => {
    if ((filteredGroups && filteredGroups.length > 0)
      || (filteredPlaces && filteredPlaces.length > 0)
      || (filteredModels && filteredModels.length > 0)
      || (filteredCompanies && filteredCompanies.length > 0)
      || (filteredAreas && filteredAreas.length > 0))
      return <Typography onClick={handleClickClearFilters} style={styles.clearFilter}>{i18n.t('clearFilterButtonManagementEquip.Typography') as string}</Typography>;
  };

  const renderSitesChips = () => (selectedSites && selectedSites.map((site: any) => (<Chip
    key={site._id}
    label={site.name}
    onDelete={() => deleteFilteredSite(site)}
    style={styles.filterChip} />)))
  const renderPlacesChips = () => (filteredPlaces && filteredPlaces.map((place) => (<Chip
    key={place.value}
    label={place.label}
    onDelete={() => deleteFilteredPlaces(place)}
    style={styles.filterChip} />)))
  const renderGroupsChips = () => (filteredGroups && filteredGroups.map((group) => (<Chip
    key={group.value}
    label={group.label}
    onDelete={() => deleteFilteredGroups(group)}
    style={styles.filterChip} />)))
  const renderAreasChips = () => (filteredAreas && filteredAreas.map((area) => (<Chip
    key={area.value}
    label={area.label}
    onDelete={() => deleteFilteredAreas(area)}
    style={styles.filterChip} />)))
  const renderModelsChips = () => (filteredModels && filteredModels.map((model) => (<Chip
    key={model.value}
    label={model.label}
    onDelete={() => deleteFilteredModels(model)}
    style={styles.filterChip} />)))
  const renderCompaniesChips = () => (filteredCompanies && filteredCompanies.map((company) => (<Chip
    key={company.value}
    label={company.label}
    onDelete={() => deleteFilteredCompanies(company)}
    style={styles.filterChip} />)))
  const renderStatusChips = () => {
    let chips = [];
    if (filteredStatus && filteredStatus.valid) chips.push(<Chip
      key="valid"
      label={i18n.t('statusOptions.valid') as string}
      onDelete={() => deleteFilteredStatus('valid')}
      style={styles.filterChip} />)
    if (filteredStatus && filteredStatus.validWithOpenedActions) chips.push(<Chip
      key="validWithOpenedActions"
      label={i18n.t('statusOptions.validWithOpenedActions') as string}
      onDelete={() => deleteFilteredStatus('validWithOpenedActions')}
      style={styles.filterChip} />)
    if (filteredStatus && filteredStatus.inactive) chips.push(<Chip
      key="inactive"
      label={i18n.t('statusOptions.inactive') as string}
      onDelete={() => deleteFilteredStatus('inactive')}
      style={styles.filterChip} />)
    if (filteredStatus && filteredStatus.maintenance) chips.push(<Chip
      key="maintenance"
      label={i18n.t('statusOptions.maintenance') as string}
      onDelete={() => deleteFilteredStatus('maintenance')}
      style={styles.filterChip} />)
    if (filteredStatus && filteredStatus.invalid.inDelay) chips.push(<Chip
      key="invalid.inDelay"
      label={i18n.t('statusOptions.inDelay') as string}
      onDelete={() => deleteFilteredStatus('invalid.inDelay')}
      style={styles.filterChip} />)
    if (filteredStatus && filteredStatus.invalid.notDone) chips.push(<Chip
      key="invalid.notDone"
      label={i18n.t('statusOptions.notDone') as string}
      onDelete={() => deleteFilteredStatus('invalid.notDone')}
      style={styles.filterChip} />)
    if (filteredStatus && filteredStatus.invalid.hasActionsOpenedOrOverdue) chips.push(<Chip
      key="invalid.hasActionsOpenedOrOverdue"
      label={i18n.t('statusOptions.hasActionsOpenedOrOverdue') as string}
      onDelete={() => deleteFilteredStatus('invalid.hasActionsOpenedOrOverdue')}
      style={styles.filterChip} />)
    if (filteredStatus && filteredStatus.invalid.hasNoServices) chips.push(<Chip
      key="hasNoServices"
      label={i18n.t('statusOptions.hasNoServices') as string}
      onDelete={() => deleteFilteredStatus('invalid.hasNoServices')}
      style={styles.filterChip} />)
    return chips;
  }

  const handleClickClearFilters = () => {
    dispatch(actions.createServiceClearAllFilters());
  };
  return (
    <Box sx={styles.filterChipContainer}>
      <Button
        variant='contained'
        onClick={() => openFiltersModal()}
        style={styles.buttonFilter}
      >
        {i18n.t('btnFilterManagementEquip.label') as string }<FilterList style={styles.buttonFilterIcon} />
      </Button>
      {renderSitesChips()}
      {renderModelsChips()}
      {renderPlacesChips()}
      {renderGroupsChips()}
      {renderAreasChips()}
      {renderCompaniesChips()}
      {renderStatusChips()}
      {clearFilterButton()}
    </Box>
  );
};

const styles = {
  chip: {
    margin: 5,
  },
  clearFilter: {
    cursor: 'pointer',
    textDecoration: 'underline',
    zIndex: 200,
    padding: '0.75rem 0.5rem',
    color: '#000',
  },
  filterChipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: '30px',
  },
  filterChip: {
    cursor: 'pointer',
    margin: 5,
  },
  buttonFilter: {
    zIndex: 200,
    backgroundColor: 'green',
    height: '2.2rem',
    width: '6.5rem',
    padding: '1rem',
    color: '#fff',
  },
  buttonFilterIcon: {
    marginLeft: 5
  },
}

export default TableFilters;